"use client"
import { FooterItemDto } from "api"
import { trackFooterClicks } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { Font } from "components/mapPages/showRooms/ShowRoomItem"
import styled from "styled-components"

export type FooterVariant = "desktop" | "mobile"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const hasLatinChars = (text: string) => /[a-zA-Z0-9]/.test(text)

// The CMS sometimes stores a bare email (no scheme) — normalise it to a mailto link.
const normalizeHref = (url: string) => {
  if (!url) return "#"
  if (EMAIL_RE.test(url)) return `mailto:${url}`
  return url
}

const isExternalUrl = (url: string) =>
  /^(https?:|tel:|mailto:)/.test(url) || url.startsWith("#")

const LinkText = styled.p<{ $variant: FooterVariant; $latin: boolean }>`
  margin: 0;
  white-space: nowrap;
  color: ${(p) =>
    p.$variant === "desktop"
      ? p.theme.colors.neutral
      : p.theme.colors.footerLinkMobile};
  font-size: ${(p) => (p.$variant === "desktop" ? "16px" : "18px")};
  font-weight: 300;
  line-height: ${(p) => (p.$variant === "desktop" ? "24px" : "1.3")};
  letter-spacing: ${(p) =>
    p.$latin ? (p.$variant === "desktop" ? "2px" : "0.96px") : "0"};
  white-space: normal;
  max-width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    white-space: nowrap;
  }
`

const ContactRow = styled.span<{ $variant: FooterVariant }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  p {
    color: ${(p) =>
      p.$variant === "desktop"
        ? p.theme.colors.footerLinkBright
        : p.theme.colors.footerContactMobile};
    font-size: ${(p) => (p.$variant === "desktop" ? "20px" : "18px")};
    font-weight: 300;
    line-height: ${(p) => (p.$variant === "desktop" ? "24px" : "1.3")};
    margin: 0;
    white-space: nowrap;
  }
  &:hover p {
    text-decoration: underline;
  }
`

const ContactIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`

const CtaButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 112px;
  height: 45px;
  padding: 0 16px;
  font-family: ${Font.OENaPro};
  border: 2px solid ${(p) => p.theme.colors.white};
  border-radius: 3px;
  color: ${(p) => p.theme.colors.white};
  font-size: 16px;
  letter-spacing: 0.8px;
  white-space: nowrap;
  cursor: pointer;
  min-width: 126px;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }
`

const PlainLink = styled.span`
  &:hover p {
    text-decoration: underline;
  }
`

type FooterItemProps = {
  item: FooterItemDto
  variant: FooterVariant
  /**
   * True when the parent column contains icon-bearing items (the "contact"
   * column). In such a column, an item *without* an icon is the CTA button
   * (e.g. chat); items *with* icons are icon links. Columns with no icons at
   * all render every item as a plain text link.
   */
  columnHasIcons: boolean
}

const FooterItem = ({ item, variant, columnHasIcons }: FooterItemProps) => {
  const iconUrl = item.icon?.imageUrl
  const href = normalizeHref(item.url)
  const external = isExternalUrl(href)
  const onClick = () => trackFooterClicks()

  // CTA button: the icon-less item inside the contact-style column.
  if (columnHasIcons && !iconUrl) {
    return (
      <NextLink href={href} openInNewTab={external} onClick={onClick}>
        <CtaButton>{item.label}</CtaButton>
      </NextLink>
    )
  }

  // Icon + label contact row (brighter text).
  if (iconUrl) {
    return (
      <NextLink href={href} openInNewTab={external} onClick={onClick}>
        <ContactRow $variant={variant}>
          <ContactIcon
            src={iconUrl}
            alt={item.icon?.alt || ""}
            aria-hidden={!item.icon?.alt}
          />
          <p>{item.label}</p>
        </ContactRow>
      </NextLink>
    )
  }

  // Plain text link (models / tools / about) — muted text.
  return (
    <NextLink href={href} openInNewTab={external} onClick={onClick}>
      <PlainLink>
        <LinkText $variant={variant} $latin={hasLatinChars(item.label)}>
          {item.label}
        </LinkText>
      </PlainLink>
    </NextLink>
  )
}

export default FooterItem
